import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api, ApiError } from '@/lib/api';
import type { User } from '@/lib/types';
import { Button, fieldStyles } from './ui';

/**
 * Soft-gate email verification: a slim strip above the page content, shown
 * only while the server can send mail and the account is unverified. The app
 * stays fully usable — this nags, it doesn't block.
 */
export function VerifyEmailBanner() {
  const { user, mailEnabled, updateUser } = useAuth();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (!user || !mailEnabled || user.emailVerified) return null;

  async function sendCode() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api<{ message: string }>('/auth/verify/request', { method: 'POST' });
      setSent(true);
      setNotice(res.message);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Couldn’t send the code.');
      // A cooldown means one is already in flight — let them type it in.
      if (err instanceof ApiError && err.message.includes('just sent')) setSent(true);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!/^\d{6}$/.test(code)) {
      setNotice('Enter the 6-digit code from the email.');
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const res = await api<{ user: User }>('/auth/verify/confirm', {
        method: 'POST',
        body: { code },
      });
      updateUser(res.user);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Verification failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        padding: '9px 16px',
        marginBottom: 14,
        borderRadius: 12,
        border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)',
        background: 'color-mix(in oklab, var(--accent) 6%, transparent)',
        fontSize: 12.5,
      }}
    >
      <span style={{ color: 'var(--text-body)', textWrap: 'pretty' }}>
        Verify your email — {sent ? `code sent to ${user.email}` : `we’ll send a code to ${user.email}`}.
      </span>
      {sent ? (
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className={fieldStyles.input}
            style={{ width: 110, textAlign: 'center', fontFamily: 'var(--font-mono)' }}
            inputMode="numeric"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void confirm();
            }}
            aria-label="Verification code"
          />
          <Button size="sm" variant="primary" onClick={() => void confirm()} disabled={busy}>
            Verify
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void sendCode()} disabled={busy}>
            Resend
          </Button>
        </span>
      ) : (
        <Button size="sm" variant="primary" onClick={() => void sendCode()} disabled={busy}>
          Send code
        </Button>
      )}
      {notice ? <span style={{ color: 'var(--muted)' }}>{notice}</span> : null}
    </div>
  );
}
